import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { PanelBottom, Plus, Trash2, RefreshCw, Save, Code, LayoutGrid } from "lucide-react";
import TenantPreferencesService, {
  FooterColumn,
  FooterConfig,
  FooterSocial,
  FooterSocialType,
} from "../../_services/TenantPreferencesService";

interface Props {
  onError: (msg: string | null) => void;
  onSuccess: (msg: string | null) => void;
}

type FooterMode = "default" | "structured" | "html";

const MAX_COLUMNS = 4;
const MAX_LINKS_PER_COLUMN = 10;
const MAX_SOCIAL = 8;
const MAX_HTML_BYTES = 100_000;

const SOCIAL_TYPES: { value: FooterSocialType; label: string }[] = [
  { value: "facebook", label: "Facebook" },
  { value: "x", label: "X (Twitter)" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "youtube", label: "YouTube" },
  { value: "website", label: "Website" },
];

const byteLength = (s: string): number => new Blob([s]).size;

/**
 * Footer tab — per-tenant ilearn footer stored in the S3 app-properties.json
 * `footer` block via GET/PUT /v1/tenant-config/footer.
 *
 * Three modes: Default (no key — ilearn shows its legacy staticfiles
 * footer.html if one exists, else a built-in footer), Structured (link
 * columns / social / copyright rendered natively with the tenant theme), and
 * Custom HTML (rendered in a sandboxed iframe, scripts disabled).
 */
const FooterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState<FooterConfig | null>(null);

  const [mode, setMode] = useState<FooterMode>("default");
  const [tagline, setTagline] = useState("");
  const [copyrightText, setCopyrightText] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [showPoweredBy, setShowPoweredBy] = useState(true);
  const [columns, setColumns] = useState<FooterColumn[]>([]);
  const [social, setSocial] = useState<FooterSocial[]>([]);
  const [html, setHtml] = useState("");
  const [confirmDefault, setConfirmDefault] = useState(false);

  const applyConfig = (cfg: FooterConfig | null) => {
    if (!cfg) {
      setMode("default");
      setTagline("");
      setCopyrightText("");
      setContactEmail("");
      setShowPoweredBy(true);
      setColumns([]);
      setSocial([]);
      setHtml("");
      return;
    }
    setMode(cfg.mode === "html" ? "html" : "structured");
    setTagline(cfg.structured?.tagline || "");
    setCopyrightText(cfg.structured?.copyrightText || "");
    setContactEmail(cfg.structured?.contactEmail || "");
    setShowPoweredBy(cfg.structured?.showPoweredBy !== false);
    setColumns(cfg.structured?.columns || []);
    setSocial(cfg.structured?.social || []);
    setHtml(cfg.html || "");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await TenantPreferencesService.getFooterConfig();
        if (cancelled) return;
        setOriginal(cfg);
        applyConfig(cfg);
      } catch (e: any) {
        onError(e?.response?.data?.error?.message || e?.message || "Failed to load footer config");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draft: FooterConfig = useMemo(() => {
    if (mode === "structured") {
      return {
        mode: "structured",
        structured: {
          ...(tagline.trim() ? { tagline: tagline.trim() } : {}),
          ...(copyrightText.trim() ? { copyrightText: copyrightText.trim() } : {}),
          ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
          showPoweredBy,
          ...(columns.length ? { columns } : {}),
          ...(social.length ? { social } : {}),
        },
      };
    }
    if (mode === "html") return { mode: "html", html };
    return { mode: "default" };
  }, [mode, tagline, copyrightText, contactEmail, showPoweredBy, columns, social, html]);

  const originalNormalized: FooterConfig = useMemo(() => {
    if (!original) return { mode: "default" };
    if (original.mode === "html") return { mode: "html", html: original.html || "" };
    return {
      mode: "structured",
      structured: {
        ...(original.structured?.tagline ? { tagline: original.structured.tagline } : {}),
        ...(original.structured?.copyrightText ? { copyrightText: original.structured.copyrightText } : {}),
        ...(original.structured?.contactEmail ? { contactEmail: original.structured.contactEmail } : {}),
        showPoweredBy: original.structured?.showPoweredBy !== false,
        ...(original.structured?.columns?.length ? { columns: original.structured.columns } : {}),
        ...(original.structured?.social?.length ? { social: original.structured.social } : {}),
      },
    };
  }, [original]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(originalNormalized);
  const htmlBytes = byteLength(html);

  // ---- column / social editors ----
  const addColumn = () =>
    setColumns((c) => [...c, { title: "", links: [{ label: "", url: "" }] }]);
  const removeColumn = (ci: number) => setColumns((c) => c.filter((_, i) => i !== ci));
  const setColumnTitle = (ci: number, title: string) =>
    setColumns((c) => c.map((col, i) => (i === ci ? { ...col, title } : col)));
  const addLink = (ci: number) =>
    setColumns((c) =>
      c.map((col, i) => (i === ci ? { ...col, links: [...col.links, { label: "", url: "" }] } : col)),
    );
  const removeLink = (ci: number, li: number) =>
    setColumns((c) =>
      c.map((col, i) => (i === ci ? { ...col, links: col.links.filter((_, j) => j !== li) } : col)),
    );
  const setLink = (ci: number, li: number, field: "label" | "url", value: string) =>
    setColumns((c) =>
      c.map((col, i) =>
        i === ci
          ? { ...col, links: col.links.map((l, j) => (j === li ? { ...l, [field]: value } : l)) }
          : col,
      ),
    );

  const addSocial = () => setSocial((s) => [...s, { type: "facebook", url: "" }]);
  const removeSocial = (si: number) => setSocial((s) => s.filter((_, i) => i !== si));
  const setSocialField = (si: number, field: "type" | "url", value: string) =>
    setSocial((s) => s.map((row, i) => (i === si ? { ...row, [field]: value } : row)));

  // ---- save ----
  const doSave = async () => {
    onError(null);
    if (mode === "html") {
      if (!html.trim()) return onError("Custom HTML footer cannot be empty");
      if (htmlBytes > MAX_HTML_BYTES) return onError("Custom HTML footer is limited to 100 KB");
    }
    setSaving(true);
    try {
      const saved = await TenantPreferencesService.saveFooterConfig(draft);
      setOriginal(saved);
      applyConfig(saved);
      onSuccess(
        mode === "default"
          ? "Footer reset to default. ilearn now shows the tenant's legacy footer.html if one exists on staticfiles, otherwise its built-in footer."
          : "Footer saved. ilearn picks it up on the next page load (app-properties.json is uploaded with no-cache — no CloudFront invalidation needed).",
      );
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to save footer");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // Resetting to default deletes the saved footer — confirm first.
    if (mode === "default" && original) {
      setConfirmDefault(true);
      return;
    }
    void doSave();
  };

  const handleRevert = () => applyConfig(original);

  if (loading) return <div className="tp-loading">Loading footer…</div>;

  const currentYear = new Date().getFullYear();
  const previewCopyright = copyrightText.trim()
    ? copyrightText.replace(/\{year\}/g, String(currentYear))
    : `© ${currentYear} Your Institution`;

  return (
    <div className="tp-footer-tab">
      {/* ---- Mode selector ---- */}
      <div className="tp-card">
        <h3>
          <PanelBottom size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
          Footer source
        </h3>
        <p className="tp-card-desc">
          The footer shows on ilearn&apos;s Privacy Policy, Terms, About Us and Control Panel pages.
          <strong> Default</strong> keeps the legacy per-tenant <code>footer.html</code> from
          staticfiles (or ilearn&apos;s built-in footer when none exists). <strong>Structured</strong>{" "}
          builds a themed footer from the fields below — it follows the tenant theme and dark mode
          automatically. <strong>Custom HTML</strong> gives full control, rendered in a sandboxed
          iframe with scripts disabled.
        </p>
        <div className="tp-appprops-toggle">
          <button
            type="button"
            className={`tp-btn ${mode === "default" ? "tp-btn-primary" : "tp-btn-secondary"}`}
            onClick={() => setMode("default")}
          >
            Default
          </button>
          <button
            type="button"
            className={`tp-btn ${mode === "structured" ? "tp-btn-primary" : "tp-btn-secondary"}`}
            onClick={() => setMode("structured")}
          >
            <LayoutGrid size={14} /> Structured
          </button>
          <button
            type="button"
            className={`tp-btn ${mode === "html" ? "tp-btn-primary" : "tp-btn-secondary"}`}
            onClick={() => setMode("html")}
          >
            <Code size={14} /> Custom HTML
          </button>
        </div>
      </div>

      {mode === "structured" && (
        <>
          {/* ---- Identity ---- */}
          <div className="tp-card">
            <h3>Identity</h3>
            <div className="tp-dimensions" style={{ maxWidth: 720 }}>
              <div>
                <label className="tp-field-label" htmlFor="tp-footer-tagline">Tagline</label>
                <input
                  id="tp-footer-tagline"
                  className="tp-field-input"
                  type="text"
                  maxLength={300}
                  placeholder="e.g. Learning for everyone"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                />
                <div className="tp-slot-hint">Optional short line under the institution name.</div>
              </div>
              <div>
                <label className="tp-field-label" htmlFor="tp-footer-copyright">Copyright line</label>
                <input
                  id="tp-footer-copyright"
                  className="tp-field-input"
                  type="text"
                  maxLength={200}
                  placeholder={`© {year} Your Institution. All rights reserved.`}
                  value={copyrightText}
                  onChange={(e) => setCopyrightText(e.target.value)}
                />
                <div className="tp-slot-hint">
                  <code>{"{year}"}</code> is replaced with the current year automatically. Leave
                  blank for &quot;© {currentYear} &lt;institution name&gt;&quot;.
                </div>
              </div>
              <div>
                <label className="tp-field-label" htmlFor="tp-footer-email">Contact email</label>
                <input
                  id="tp-footer-email"
                  className="tp-field-input"
                  type="email"
                  maxLength={120}
                  placeholder="support@yourinstitution.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
                <div className="tp-slot-hint">Optional — shown as a mailto link.</div>
              </div>
            </div>
            <div className="tp-feature-row" style={{ maxWidth: 720 }}>
              <div className="tp-feature-text">
                <div className="tp-feature-label">Show &quot;Powered by ConceptWaves&quot;</div>
                <div className="tp-slot-hint">Small attribution line at the bottom of the footer.</div>
              </div>
              <button
                type="button"
                className={`tp-switch ${showPoweredBy ? "on" : ""}`}
                role="switch"
                aria-checked={showPoweredBy}
                aria-label={`Powered by ConceptWaves ${showPoweredBy ? "shown" : "hidden"}`}
                onClick={() => setShowPoweredBy((v) => !v)}
              >
                <span className="tp-switch-knob" />
              </button>
            </div>
          </div>

          {/* ---- Link columns ---- */}
          <div className="tp-card">
            <h3>Link columns</h3>
            <p className="tp-card-desc">
              Up to {MAX_COLUMNS} columns of links (e.g. Quick Links, Support). Use full URLs for
              external sites or paths like <code>/about-us</code> for pages inside ilearn.
            </p>
            {columns.map((col, ci) => (
              <div key={ci} className="tp-footer-column-block">
                <div className="tp-footer-column-head">
                  <input
                    className="tp-field-input"
                    type="text"
                    maxLength={60}
                    placeholder="Column title (e.g. Quick Links)"
                    value={col.title}
                    onChange={(e) => setColumnTitle(ci, e.target.value)}
                  />
                  <button
                    type="button"
                    className="tp-btn tp-btn-danger-text"
                    onClick={() => removeColumn(ci)}
                    title="Remove column"
                    aria-label="Remove column"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {col.links.map((link, li) => (
                  <div key={li} className="tp-footer-link-row">
                    <input
                      className="tp-field-input"
                      type="text"
                      maxLength={60}
                      placeholder="Label"
                      value={link.label}
                      onChange={(e) => setLink(ci, li, "label", e.target.value)}
                    />
                    <input
                      className="tp-field-input"
                      type="text"
                      maxLength={500}
                      placeholder="https://… or /about-us"
                      value={link.url}
                      onChange={(e) => setLink(ci, li, "url", e.target.value)}
                    />
                    <button
                      type="button"
                      className="tp-btn tp-btn-danger-text"
                      onClick={() => removeLink(ci, li)}
                      title="Remove link"
                      aria-label="Remove link"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary"
                  onClick={() => addLink(ci)}
                  disabled={col.links.length >= MAX_LINKS_PER_COLUMN}
                >
                  <Plus size={14} /> Add link
                </button>
              </div>
            ))}
            <div className="tp-slot-actions" style={{ marginTop: columns.length ? 12 : 0 }}>
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={addColumn}
                disabled={columns.length >= MAX_COLUMNS}
              >
                <Plus size={14} /> Add column
              </button>
            </div>
          </div>

          {/* ---- Social links ---- */}
          <div className="tp-card">
            <h3>Social links</h3>
            <p className="tp-card-desc">Shown as icons in the footer. Up to {MAX_SOCIAL}.</p>
            {social.map((row, si) => (
              <div key={si} className="tp-footer-link-row">
                <select
                  className="tp-field-input"
                  value={row.type}
                  onChange={(e) => setSocialField(si, "type", e.target.value)}
                  aria-label="Social network"
                >
                  {SOCIAL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  className="tp-field-input"
                  type="text"
                  maxLength={500}
                  placeholder="https://…"
                  value={row.url}
                  onChange={(e) => setSocialField(si, "url", e.target.value)}
                />
                <button
                  type="button"
                  className="tp-btn tp-btn-danger-text"
                  onClick={() => removeSocial(si)}
                  title="Remove social link"
                  aria-label="Remove social link"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="tp-slot-actions" style={{ marginTop: social.length ? 12 : 0 }}>
              <button
                type="button"
                className="tp-btn tp-btn-primary"
                onClick={addSocial}
                disabled={social.length >= MAX_SOCIAL}
              >
                <Plus size={14} /> Add social link
              </button>
            </div>
          </div>

          {/* ---- Preview ---- */}
          <div className="tp-card">
            <h3>Preview</h3>
            <p className="tp-card-desc">
              Approximate — ilearn renders this with the tenant&apos;s theme colors and dark mode.
            </p>
            <div className="tp-footer-preview">
              {tagline.trim() && <div className="tp-footer-preview-tagline">{tagline}</div>}
              {columns.length > 0 && (
                <div className="tp-footer-preview-columns">
                  {columns.map((col, ci) => (
                    <div key={ci}>
                      {col.title && <div className="tp-footer-preview-coltitle">{col.title}</div>}
                      {col.links
                        .filter((l) => l.label.trim())
                        .map((l, li) => (
                          <div key={li} className="tp-footer-preview-link">{l.label}</div>
                        ))}
                    </div>
                  ))}
                </div>
              )}
              {social.length > 0 && (
                <div className="tp-footer-preview-social">
                  {social
                    .filter((s) => s.url.trim())
                    .map((s, si) => (
                      <span key={si} className="tp-footer-preview-socialpill">{s.type}</span>
                    ))}
                </div>
              )}
              <div className="tp-footer-preview-copyright">
                {previewCopyright}
                {contactEmail.trim() && <> · {contactEmail}</>}
              </div>
              {showPoweredBy && (
                <div className="tp-footer-preview-powered">Powered by ConceptWaves</div>
              )}
            </div>
          </div>
        </>
      )}

      {mode === "html" && (
        <div className="tp-card">
          <h3>Custom HTML</h3>
          <p className="tp-card-desc">
            Rendered inside a sandboxed iframe in ilearn — scripts are disabled, links work.
            Include your own inline styles; the tenant theme does not apply inside the iframe.
          </p>
          <textarea
            className="tp-appprops-textarea"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={16}
            spellCheck={false}
            placeholder="<div style=&quot;…&quot;>…</div>"
          />
          <div
            className="tp-slot-hint"
            style={htmlBytes > MAX_HTML_BYTES ? { color: "var(--status-danger, #dc3545)" } : undefined}
          >
            {(htmlBytes / 1024).toFixed(1)} KB of 100 KB
          </div>
          {html.trim() && (
            <div className="tp-appprops-preview">
              <div className="tp-slot-hint" style={{ marginBottom: 6 }}>Preview</div>
              <iframe
                title="Footer preview"
                srcDoc={html}
                sandbox=""
                style={{ width: "100%", height: 260, border: "1px solid var(--border-default)", borderRadius: 8, background: "#fff" }}
              />
            </div>
          )}
        </div>
      )}

      {mode === "default" && (
        <div className="tp-card">
          <h3>Default behavior</h3>
          <p className="tp-card-desc">
            No footer is stored for this tenant. ilearn shows the legacy{" "}
            <code>footer.html</code> from staticfiles.edusquares.com if one was uploaded for this
            tenant; otherwise it shows a built-in footer with the institution name, copyright and
            Privacy / Terms links. Note: editing the legacy footer.html requires a CloudFront
            invalidation — the Structured and Custom HTML modes here do not.
          </p>
        </div>
      )}

      {/* ---- Toolbar ---- */}
      <div className="tp-toolbar">
        {dirty && <span className="tp-dirty-flag">Unsaved changes</span>}
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={handleRevert}
          disabled={!dirty || saving}
        >
          <RefreshCw size={14} /> Revert
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* ---- Reset-to-default confirmation ---- */}
      <Modal show={confirmDefault} onHide={() => setConfirmDefault(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset footer to default?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This deletes the saved {original?.mode === "html" ? "custom HTML" : "structured"} footer
          for this tenant. ilearn will fall back to the legacy footer.html on staticfiles (if one
          exists) or its built-in footer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDefault(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setConfirmDefault(false);
              void doSave();
            }}
          >
            Reset to default
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FooterTab;
